import { Form, Modal, Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, {ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import { loanDrawdownGetAction, loanDrawdownQsInstructedAction } from '~Loans/actions';
import { loanDrawdownSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';

export default function DrawdownQsInstructedModal(props: {
    loanDrawdownUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { loanDrawdownUuid, isOpen, onCancel } = props;

    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));

    const [ qsInstructedTime, setQsInstructedTime ] = useState<string>(null);
    const [ errors, setErrors] = useState<IDictionary<string>>({});

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdown) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        drawdown,
        dispatch,
        loanDrawdownUuid,
    ]);

    const validateQsInstructedTime: () => boolean = useCallback(() => {
        let error: string = null;

        if (!qsInstructedTime) {
            error = 'Please enter the date the QS was instructed';
        }

        setErrors({
            ...errors,
            qsInstructedTime: error,
        });

        return !error;
    }, [
        errors,
        qsInstructedTime,
    ]);

    const disabledTime: (time: Dayjs) => boolean = useCallback((time: Dayjs) => {
        return time && (time.isAfter(dayjs(), 'second') || time.isBefore(dayjs(drawdown.requestedTime)));
    }, [drawdown.requestedTime]);

    const onClickOk: () => void = useCallback(() => {
        if (!validateQsInstructedTime()) {
            return;
        }

        dispatch(loanDrawdownQsInstructedAction(drawdown.uuid, qsInstructedTime));
        onCancel();
    }, [
        drawdown,
        dispatch,
        qsInstructedTime,
        onCancel,
        validateQsInstructedTime,
    ]);

    const onChangeQsInstructedDate: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setQsInstructedTime(date ? dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(qsInstructedTime || undefined).format('HH:mm')}`).format() : null);
    }, [qsInstructedTime]);

    const onChangeQsInstructedTime: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setQsInstructedTime(time ? dayjs(`${dayjs(qsInstructedTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format() : null);
    }, [qsInstructedTime]);

    if (!drawdown) {
        return (
            <Modal
                footer={false}
                onCancel={onCancel}
                open={isOpen}
                title='QS Instructed'
                wrapClassName='loan-drawdown-qs-instructed-modal'
                width={640}
            >
                <Spin/>
            </Modal>
        );
    }

    return (
        <Modal
            okText='QS Instructed'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='QS Instructed'
            wrapClassName='loan-drawdown-qs-instructed-modal'
            width={640}
        >
            <Form.Item
                label='Date QS Instructed'
                className='date-qs-instructed'
                help={errors.qsInstructedTime}
                validateStatus={errors.qsInstructedTime && 'error'}
            >
                <Space>
                    <DatePicker
                        className='qs-instructed-pick-date'
                        disabledDate={disabledTime}
                        onChange={onChangeQsInstructedDate}
                        format='DD/MM/YYYY'
                        value={qsInstructedTime ? dayjs(qsInstructedTime) : null}
                    />
                    <TimePicker
                        className='qs-instructed-pick-time'
                        disabledDate={disabledTime}
                        onChange={onChangeQsInstructedTime}
                        value={qsInstructedTime ? dayjs(qsInstructedTime) : null}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
        </Modal>
    );
}